import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  useFreshdeskEmailIntegrationAutoSetupMutation,
  useGetFreshdeskEmailAgentsQuery,
  useGetFreshdeskEmailGroupsQuery,
  useGetProjectByIdQuery,
} from 'src/services';
import { useAppSelector } from 'src/store';
import { ProductsTypeBack } from 'src/constants/products';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { Skeleton } from 'src/components/ui/skeleton';
import { useForm } from 'react-hook-form';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { AgentsSelect } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/AgentsSelect/AgentsSelect';
import { EscalationGroupSelect } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/EscalationGroupSelect/EscalationGroupSelect';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const FreshdeskEmailStep3 = ({
  onClick = () => {},
  setNextButtonDisabled = () => {},
}: {
  onClick?: (integration?: SelectedIntegration) => void;
  setNextButtonDisabled?: (disabled: boolean) => void;
}) => {
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  const {
    data: agentsResponse,
    isLoading: isLoadingAgents,
    isError: isErrorAgents,
  } = useGetFreshdeskEmailAgentsQuery(
    {
      projectId: id,
      integrationId: selectedIntegration?.id,
      type: ProductsTypeBack[currentProject?.project_type],
    },
    { skip: !selectedIntegration?.id || !currentProject },
  );

  const { data: groupsResponse, isLoading: isLoadingGroups } = useGetFreshdeskEmailGroupsQuery(
    {
      projectId: id,
      integrationId: selectedIntegration?.id,
      type: ProductsTypeBack[currentProject?.project_type],
    },
    { skip: !selectedIntegration?.id || !currentProject },
  );

  const [setFreshdeskAutoSetup, { isLoading: isLoadingSetFreshdeskAutoSetup }] =
    useFreshdeskEmailIntegrationAutoSetupMutation();

  useEffect(() => {
    if (agentsResponse) {
      setAgents([...agentsResponse]);
    }
    if (groupsResponse) {
      setGroups([...groupsResponse]);
    }
  }, [agentsResponse, groupsResponse]);

  const { handleSubmit, control, setValue } = useForm({
    defaultValues: {
      responder_id: '',
      responder_email: '',
      escalate_assign_group_id: '',
    },
    resolver: formResolver(
      object({
        responder_id: stringScheme({ required: true }).label('Responder Id'),
        escalate_assign_group_id: stringScheme({ required: false }).label('Escalate As'),
      }),
    ),
  });

  useEffect(() => {
    if (agentsResponse) {
      setAgents([...agentsResponse]);
    }
    if (groupsResponse) {
      setGroups([...groupsResponse]);
    }
  }, [agentsResponse, groupsResponse]);

  useLoaderWhile(isLoadingAgents, isLoadingGroups, isLoadingSetFreshdeskAutoSetup);

  useEffect(() => {
    if (isErrorAgents) {
      setNextButtonDisabled(true);
    }
  }, [isErrorAgents]);

  // The agent and group selects store numeric ids.
  const submit = async (data: {
    responder_id: number | string;
    responder_email: string;
    escalate_assign_group_id: number | string;
  }) => {
    const request: {
      projectId: string;
      integrationId: number | string;
      body: Record<string, unknown>;
    } = {
      projectId: id,
      integrationId: selectedIntegration.id,
      body: {
        responder_id: +data.responder_id,
        responder_email: data.responder_email,
      },
    };

    if (data.escalate_assign_group_id) {
      request.body.escalate_assign_group_id = +data.escalate_assign_group_id;
    }

    const setFreshdeskAutoSetupResult = await setFreshdeskAutoSetup(request);
    if (!('error' in setFreshdeskAutoSetupResult)) {
      onClick({
        ...selectedIntegration,
        integration_data: {
          ...selectedIntegration.integration_data,
          is_setup: true,
        },
      });
    }
  };

  return (
    <form id="freshdesk-step-3-form" onSubmit={handleSubmit(submit)}>
      <div className="content">
        <LoaderWrapper
          loader={<Skeleton className="w-full h-16" />}
          isLoading={isLoadingAgents || isLoadingGroups || isLoadingSetFreshdeskAutoSetup}
        >
          {!isErrorAgents ? (
            <div className={'flex flex-col gap-2.5'}>
              {agents.length > 0 && (
                <AgentsSelect
                  label={
                    <div className={'flex flex-row gap-2 items-center mb-1'}>
                      Freshdesk account for AI{' '}
                      <InputInfoToolTip
                        infoToolTip={{
                          side: 'right',
                          description: <>AI will use this account to handle tickets.</>,
                        }}
                      />
                    </div>
                  }
                  control={control}
                  setValue={setValue}
                  name={'responder_id'}
                  options={agents}
                />
              )}
              {groups.length > 0 && (
                <EscalationGroupSelect
                  label={
                    <div className={'flex flex-row gap-2 items-center mb-1'}>
                      Escalation group{' '}
                      <InputInfoToolTip
                        infoToolTip={{
                          side: 'right',
                          description: <>AI will assign escalated tickets to this group.</>,
                        }}
                      />
                    </div>
                  }
                  control={control}
                  name={'escalate_assign_group_id'}
                  options={groups}
                />
              )}
            </div>
          ) : (
            <p>
              The Freshdesk details you entered seem invalid. Please double-check your subdomain,
              and API token following{' '}
              <a href={selectedIntegration.setupGuideUrl} target="_blank" rel="noreferrer">
                this guide
              </a>
              {'.'}
            </p>
          )}
        </LoaderWrapper>
      </div>
    </form>
  );
};
